/**
 * Global offline banner – pinned to the top of the viewport.
 * Shows when navigator.onLine === false. Cached departure/route data
 * is served separately by departureCache + sw.js.
 */
import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export function OfflineBanner() {
  const { t } = useLanguage();
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const setDebounced = (value: boolean) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsOnline(value), 250);
    };
    const onOnline = () => setDebounced(true);
    const onOffline = () => setDebounced(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="global-offline-banner"
      className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-2 px-3 py-2 bg-amber-500/95 text-amber-950 text-xs font-medium shadow-md backdrop-blur-sm"
      style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top, 0px))' }}
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{t('offline_banner')}</span>
    </div>
  );
}
